import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../api/api';
import AuthContext from '../../contexts/AuthContext';
import { formatDate } from '../../utils/dateUtils';
import '../../styles/CreateAssignmentPage.css';

const StudentPlanProgressPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // State
  const [plan, setPlan] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch plan details on mount
  useEffect(() => {
    fetchPlanDetails();
  }, [planId]);

  const fetchPlanDetails = async () => {
    try {
      setLoading(true);

      // Fetch plan details - backend validates access and returns 403 if unauthorized
      const response = await api.get(`/learning-plans/${planId}`);
      const planData = response.data.data || response.data;

      // Secondary client-side validation (backend is primary)
      // If user is parent, verify they have access to this student
      if (user.role === 'parent') {
        const childrenResponse = await api.get(`/parents/children/${user.id}`);
        const children = childrenResponse.data.data || [];
        const hasAccess = children.some(child => child._id === planData.studentId._id || child._id === planData.studentId);

        if (!hasAccess) {
          setError('You do not have permission to view this learning plan');
          setLoading(false);
          return;
        }
      }

      // If user is student, verify this is their plan
      if (user.role === 'student') {
        if (planData.studentId._id !== user.id && planData.studentId !== user.id) {
          setError('You do not have permission to view this learning plan');
          setLoading(false);
          return;
        }
      }

      setPlan(planData);

      // Fetch assignments for this plan
      const assignmentsResponse = await api.get(`/learning-plans/${planId}/assignments`);
      setAssignments(assignmentsResponse.data.data || []);
    } catch (error) {
      console.error('Error fetching plan details:', error);
      // Handle 403 Forbidden errors specifically
      if (error.response?.status === 403) {
        setError('You do not have permission to view this learning plan');
      } else {
        setError('Failed to load learning plan details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get goal display text
  const getGoalDisplay = (goal) => {
    if (goal.type === 'pages') {
      return {
        title: 'Pages Goal',
        detail: `Pages ${goal.pages.from}-${goal.pages.to}`,
        total: `${goal.pages.total} pages`
      };
    } else if (goal.type === 'verses') {
      return {
        title: 'Verses Goal',
        detail: `${goal.verses.total} verses`,
        total: `Starting from Surah ${goal.verses.startFrom.surah}, Verse ${goal.verses.startFrom.verse}`
      };
    } else if (goal.type === 'juz') {
      return {
        title: 'Juz Goal',
        detail: `Juz ${goal.juz.number}`,
        total: `${goal.juz.versesCount} verses`
      };
    } else if (goal.type === 'surahs') {
      return {
        title: 'Surahs Goal',
        detail: `${goal.surahs.length} surahs`,
        total: goal.surahs.map(s => s.name).join(', ')
      };
    }
    return { title: 'Unknown', detail: '', total: '' };
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return '#4caf50';
      case 'paused':
        return '#ff9800';
      case 'completed':
        return '#2196f3';
      case 'cancelled':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  // Get progress bar color
  const getProgressColor = (percent) => {
    if (percent >= 75) return '#4caf50';
    if (percent >= 50) return '#2196f3';
    if (percent >= 25) return '#ff9800';
    return '#f44336';
  };

  // Get assignment content display
  const getAssignmentContent = (assignment) => {
    if (assignment.pages) {
      if (assignment.pages.from === assignment.pages.to) {
        return `Page ${assignment.pages.from}`;
      }
      return `Pages ${assignment.pages.from}-${assignment.pages.to}`;
    }

    if (assignment.range) {
      const { start, end } = assignment.range;
      if (start.surah.id === end.surah.id) {
        return `${start.surah.name} ${start.verse}-${end.verse}`;
      }
      return `${start.surah.name} ${start.verse} to ${end.surah.name} ${end.verse}`;
    }

    if (assignment.surah) {
      return `Surah ${assignment.surah.name}`;
    }

    return assignment.title;
  };

  // Get assignment status badge and icon
  const getAssignmentStatus = (assignment) => {
    if (assignment.assessments && assignment.assessments.length > 0) {
      const latestAssessment = assignment.assessments[assignment.assessments.length - 1];
      const rating = latestAssessment.rating;

      let color = '#4caf50';
      let icon = '✓';

      if (rating === 'Excellent') {
        color = '#4caf50';
        icon = '⭐';
      } else if (rating === 'Very Good') {
        color = '#2196f3';
        icon = '✓';
      } else if (rating === 'Good') {
        color = '#03a9f4';
        icon = '✓';
      } else if (rating === 'Pass') {
        color = '#ff9800';
        icon = '✓';
      } else if (rating === 'Need to Repeat') {
        color = '#f44336';
        icon = '⟳';
      }

      return {
        text: rating,
        color,
        icon,
        isCompleted: true
      };
    }

    const isPastDue = new Date(assignment.dueDate) < new Date();
    if (isPastDue) {
      return {
        text: 'Past Due',
        color: '#f44336',
        icon: '!',
        isCompleted: false
      };
    }

    return {
      text: 'Pending',
      color: '#999',
      icon: '○',
      isCompleted: false
    };
  };

  // Get milestone icon based on progress
  const getMilestoneIcon = (percent) => {
    if (percent === 0) return '🎯';
    if (percent < 25) return '🌱';
    if (percent < 50) return '🌿';
    if (percent < 75) return '🌳';
    if (percent < 100) return '🏆';
    return '✨';
  };

  if (loading) {
    return (
      <div className="create-assignment-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>Loading your learning plan...</p>
        </div>
      </div>
    );
  }

  if (!plan || error) {
    return (
      <div className="create-assignment-container">
        <div className="header">
          <h1>Learning Plan</h1>
          <Link to={user.role === 'parent' ? '/dashboard' : '/student-dashboard'} className="back-btn">
            ← Back to Dashboard
          </Link>
        </div>
        <div className="form-card">
          <div className="form-section" style={{textAlign: 'center', padding: '60px 20px'}}>
            <p style={{color: '#666'}}>
              {error || 'The learning plan you\'re looking for doesn\'t exist or has been deleted.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const goalInfo = getGoalDisplay(plan.goal);
  const completedAssignments = assignments.filter(a => a.assessments && a.assessments.length > 0).length;

  return (
    <div className="create-assignment-container">
      <div className="header">
        <h1>My Learning Plan</h1>
        <Link
          to={user.role === 'parent' ? '/dashboard' : '/student-dashboard'}
          className="back-btn"
        >
          ← Back to Dashboard
        </Link>
      </div>

      {/* Motivational Header */}
      <div className="form-card" style={{
        marginBottom: '20px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div className="form-section">
          <div style={{textAlign: 'center', padding: '20px 0'}}>
            <div style={{fontSize: '48px', marginBottom: '10px'}}>
              {getMilestoneIcon(plan.progress?.percent || 0)}
            </div>
            <h2 style={{margin: '0 0 10px 0', fontSize: '24px', color: 'white'}}>
              {plan.studentId?.name || 'Student'}'s Learning Journey
            </h2>
            <div style={{fontSize: '16px', opacity: 0.9}}>
              {goalInfo.detail}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Summary */}
      <div className="form-card" style={{marginBottom: '20px'}}>
        <div className="form-section">
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px'}}>
            <h3 style={{margin: 0}}>Progress Overview</h3>
            <span
              style={{
                padding: '6px 14px',
                backgroundColor: getStatusColor(plan.status),
                color: 'white',
                borderRadius: '14px',
                fontSize: '13px',
                fontWeight: '500',
                textTransform: 'capitalize'
              }}
            >
              {plan.status}
            </span>
          </div>

          {/* Progress Bar */}
          <div style={{marginBottom: '25px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
              <span style={{fontSize: '14px', fontWeight: '500', color: '#555'}}>Overall Completion</span>
              <span style={{fontSize: '20px', fontWeight: '700', color: getProgressColor(plan.progress?.percent || 0)}}>
                {Math.round(plan.progress?.percent || 0)}%
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '16px',
              backgroundColor: '#e0e0e0',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                width: `${plan.progress?.percent || 0}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${getProgressColor(plan.progress?.percent || 0)} 0%, ${getProgressColor(plan.progress?.percent || 0)}dd 100%)`,
                transition: 'width 0.5s ease',
                boxShadow: '0 0 10px rgba(76, 175, 80, 0.3)'
              }} />
            </div>
          </div>

          {/* Stats Grid */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px'}}>
            <div style={{textAlign: 'center', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px'}}>
              <div style={{fontSize: '28px', fontWeight: '700', color: '#0277bd', marginBottom: '5px'}}>
                {completedAssignments}
              </div>
              <div style={{fontSize: '13px', color: '#666'}}>Completed</div>
            </div>
            <div style={{textAlign: 'center', padding: '15px', backgroundColor: '#fff8e1', borderRadius: '8px'}}>
              <div style={{fontSize: '28px', fontWeight: '700', color: '#f57c00', marginBottom: '5px'}}>
                {assignments.length - completedAssignments}
              </div>
              <div style={{fontSize: '13px', color: '#666'}}>Remaining</div>
            </div>
            <div style={{textAlign: 'center', padding: '15px', backgroundColor: '#f3e5f5', borderRadius: '8px'}}>
              <div style={{fontSize: '28px', fontWeight: '700', color: '#7b1fa2', marginBottom: '5px'}}>
                {assignments.length}
              </div>
              <div style={{fontSize: '13px', color: '#666'}}>Total Assignments</div>
            </div>
          </div>
        </div>
      </div>

      {/* Goal Details */}
      <div className="form-card" style={{marginBottom: '20px'}}>
        <div className="form-section">
          <h3 style={{marginBottom: '15px'}}>Goal Details</h3>
          <div style={{
            padding: '15px',
            backgroundColor: '#f5f7fa',
            borderRadius: '8px',
            borderLeft: '4px solid #667eea'
          }}>
            <div style={{fontSize: '15px', fontWeight: '600', color: '#333', marginBottom: '8px'}}>
              {goalInfo.title}
            </div>
            <div style={{fontSize: '14px', color: '#666', marginBottom: '5px'}}>
              {goalInfo.detail}
            </div>
            <div style={{fontSize: '13px', color: '#888'}}>
              {goalInfo.total}
            </div>
          </div>

          {/* Timeline */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginTop: '15px'}}>
            <div>
              <div style={{fontSize: '12px', color: '#999', marginBottom: '5px'}}>Start Date</div>
              <div style={{fontSize: '14px', fontWeight: '500', color: '#333'}}>{formatDate(plan.timeline.start)}</div>
            </div>
            <div>
              <div style={{fontSize: '12px', color: '#999', marginBottom: '5px'}}>End Date</div>
              <div style={{fontSize: '14px', fontWeight: '500', color: '#333'}}>{formatDate(plan.timeline.end)}</div>
            </div>
          </div>

          {/* Schedule */}
          <div style={{marginTop: '15px'}}>
            <div style={{fontSize: '12px', color: '#999', marginBottom: '5px'}}>Practice Schedule</div>
            <div style={{fontSize: '14px', fontWeight: '500', color: '#333'}}>
              {plan.schedule.specificDays.join(', ')} ({plan.schedule.daysPerWeek} days/week)
            </div>
          </div>

          {/* Notes */}
          {plan.notes && (
            <div style={{marginTop: '15px', padding: '12px', backgroundColor: '#fff3e0', borderRadius: '6px', borderLeft: '4px solid #ff9800'}}>
              <div style={{fontSize: '12px', fontWeight: '600', color: '#e65100', marginBottom: '5px'}}>TEACHER'S NOTE</div>
              <div style={{fontSize: '13px', color: '#666', whiteSpace: 'pre-wrap'}}>{plan.notes}</div>
            </div>
          )}
        </div>
      </div>

      {/* Assignments List */}
      <div className="form-card">
        <div className="form-section">
          <h3 style={{marginBottom: '15px'}}>Assignments Timeline</h3>

          {assignments.length === 0 ? (
            <div style={{textAlign: 'center', padding: '40px', color: '#999'}}>
              <div style={{fontSize: '48px', marginBottom: '15px'}}>📝</div>
              <p>No assignments yet. Your teacher will create them soon!</p>
            </div>
          ) : (
            <div style={{position: 'relative', paddingLeft: '30px'}}>
              {/* Timeline line */}
              <div style={{
                position: 'absolute',
                left: '10px',
                top: '15px',
                bottom: '15px',
                width: '3px',
                backgroundColor: '#e0e0e0'
              }} />

              <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                {assignments.map((assignment, index) => {
                  const status = getAssignmentStatus(assignment);

                  return (
                    <div
                      key={assignment._id}
                      style={{
                        position: 'relative',
                        padding: '15px',
                        border: `2px solid ${status.isCompleted ? status.color : '#e0e0e0'}`,
                        borderRadius: '10px',
                        backgroundColor: status.isCompleted ? `${status.color}10` : '#fafafa',
                        transition: 'all 0.2s'
                      }}
                    >
                      {/* Timeline dot */}
                      <div style={{
                        position: 'absolute',
                        left: '-30px',
                        top: '20px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: status.color,
                        border: '3px solid white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: 'white',
                        fontWeight: '700',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}>
                        {status.icon}
                      </div>

                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px'}}>
                        <div style={{flex: 1}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px'}}>
                            <span style={{fontSize: '13px', fontWeight: '600', color: '#999'}}>#{index + 1}</span>
                            <span style={{fontSize: '15px', fontWeight: '600', color: '#333'}}>{assignment.title}</span>
                          </div>
                          <div style={{fontSize: '14px', color: '#666', marginBottom: '8px'}}>
                            {getAssignmentContent(assignment)}
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px'}}>
                            <span style={{color: '#888'}}>
                              <strong>Due:</strong> {formatDate(assignment.dueDate)}
                            </span>
                            <span style={{color: '#888', textTransform: 'capitalize'}}>
                              <strong>Type:</strong> {assignment.type.replace('-', ' ')}
                            </span>
                          </div>
                        </div>
                        <div style={{
                          padding: '6px 12px',
                          backgroundColor: status.color,
                          color: 'white',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}>
                          {status.text}
                        </div>
                      </div>

                      {/* Assessment details if available */}
                      {assignment.assessments && assignment.assessments.length > 0 && (
                        <div style={{
                          marginTop: '10px',
                          paddingTop: '10px',
                          borderTop: '1px solid #e0e0e0',
                          fontSize: '13px',
                          color: '#666'
                        }}>
                          <strong>Feedback:</strong> {assignment.assessments[assignment.assessments.length - 1].notes || 'No feedback provided'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Encouragement Message */}
      {assignments.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '20px',
          background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
          borderRadius: '12px',
          textAlign: 'center',
          border: '2px solid #4caf50'
        }}>
          <div style={{fontSize: '24px', marginBottom: '10px'}}>
            {plan.progress?.percent >= 75 ? '🎉' : plan.progress?.percent >= 50 ? '💪' : plan.progress?.percent >= 25 ? '🌟' : '🚀'}
          </div>
          <div style={{fontSize: '16px', fontWeight: '600', color: '#2e7d32', marginBottom: '5px'}}>
            {plan.progress?.percent >= 75
              ? 'Almost there! Keep up the excellent work!'
              : plan.progress?.percent >= 50
                ? 'Great progress! You\'re halfway through!'
                : plan.progress?.percent >= 25
                  ? 'Good start! Keep going strong!'
                  : 'Begin your journey with determination!'}
          </div>
          <div style={{fontSize: '13px', color: '#558b2f'}}>
            May Allah make it easy for you and accept your efforts
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentPlanProgressPage;
